import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import Widgets from "Components/Common/Widgets";
import { useMutation, useQuery } from "@tanstack/react-query";
import Heading from "Components/Common/Heading";
import Button from "Components/Common/Button";
import Filter from "Components/Common/Filter";
import Table, { Column } from "Components/Common/Table";
import AddGuestReqModal from "Components/Modals/AddGuestReqModal";
import {
  ApproveGuestPass,
  GenerateNewOtp,
  GetGuestAccessStats,
  GetGuestRequests,
  RejectGuestPass,
  WithdrawGuestAccess,
} from "endpoints/guestAccess/index.action";
import Spinner from "Components/Common/Spinner";
import ViewGuestDetailsModal from "Components/Modals/ViewGuestDetailsModal";
import Pills from "Components/Common/Pills";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const AccessRequests = () => {
  const { t } = useTranslation();
  document.title = t("accessRequests.title");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [lastSearch, setLastSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [accessData, setAccessData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("accepted");
  
  // Initialize stats with translated labels
  const [statsData, setStatsData] = useState<any>([
    {
      id: 1,
      label: t("accessRequests.totalRequests"),
      counter: "0",
      bgColor: "#007BFF14",
    },
    {
      id: 2,
      label: t("accessRequests.usedRequests"),
      counter: "0",
      badge: "ri-arrow-up-s-fill",
      badgeColor: "success",
      bgColor: "#FF6A0014",
    },
    {
      id: 3,
      label: t("accessRequests.activeRequests"),
      counter: "0",
      bgColor: "#28C76F14",
    },
    {
      id: 4,
      label: t("accessRequests.rejectedRequests"),
      counter: "0",
      bgColor: "#FF3B3014",
    },
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [isViewGuestDetailsModalOpen, setIsViewGuestDetailsModalOpen] =
    useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [loadingActions, setLoadingActions] = useState<
    Record<string, "withdraw" | "generateOtp" | "approve" | "reject" | undefined>
  >({});

  console.log("accessData", accessData);

  // Update labels when language changes
  useEffect(() => {
    setStatsData((prev: any[]) =>
      prev.map((widget) => {
        if (widget.id === 1) {
          return { ...widget, label: t("accessRequests.totalRequests") };
        }
        if (widget.id === 2) {
          return { ...widget, label: t("accessRequests.usedRequests") };
        }
        if (widget.id === 3) {
          return { ...widget, label: t("accessRequests.activeRequests") };
        }
        if (widget.id === 4) {
          return { ...widget, label: t("accessRequests.rejectedRequests") };
        }
        return widget;
      })
    );
  }, [t]);

  const { isFetching } = useQuery({
    queryKey: ["stats"],
    gcTime: 0,
    queryFn: async () => {
      const res = await GetGuestAccessStats();
      if (res?.statusCode === 200 && res.data) {
        setStatsData((prev: any[]) =>
          prev.map((widget) => {
            if (widget.id === 1) {
              return { ...widget, label: t("accessRequests.totalRequests"), counter: res.data.total || 0 };
            }
            if (widget.id === 2) {
              return { ...widget, label: t("accessRequests.usedRequests"), counter: res.data.used || 0 };
            }
            if (widget.id === 3) {
              return { ...widget, label: t("accessRequests.activeRequests"), counter: res.data.active || 0 };
            }
            if (widget.id === 4) {
              return { ...widget, label: t("accessRequests.rejectedRequests"), counter: res.data.rejected || 0 };
            }
            return widget;
          })
        );

        return res.data;
      }
      throw new Error(t("accessRequests.failedToFetchStats"));
    },
  });

  const getRequestsMutation = useMutation({
    mutationKey: ["guestRequests"],
    mutationFn: async ({
      page,
      limit,
      search,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await GetGuestRequests({ page, limit, search });
      if (res?.statusCode === 200 && res.data) {
        const formattedData = res.data.guestPasses.map((req: any) => {
          // Store raw end date and time for expiration checking
          const endDate = req?.endDate ? new Date(req.endDate) : null;
          const endTime = req?.endTime || "23:59";
          let endDateTime: Date | null = null;
          
          if (endDate && endTime) {
            const [hours, minutes] = endTime.split(":").map(Number);
            endDateTime = new Date(endDate);
            endDateTime.setHours(hours || 23, minutes || 59, 0, 0);
          }

          return {
            id: req.id,
            guest: req.guestName || "N/A",
            start: req?.startDate
              ? new Date(req.startDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }) +
                " -" +
                req.startTime
              : "N/A",
            end: req?.endDate
              ? new Date(req.endDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }) +
                "-" +
                req.endTime
              : "N/A",
            otp: req.code,
            requestedBy: [req.resident?.firstName, req.resident?.lastName]
              .filter(Boolean)
              .join(" "),
            condo: req.resident?.residentProfile?.unit,
            req: new Date(req.createdAt).toLocaleString(),
            status: req.status
              ? req.status.charAt(0).toUpperCase() + req.status.slice(1)
              : "N/A",
            purposeOfVisit: req.purposeOfVisit || "N/A",
            requesteeProfileUrl: req.resident?.profileImage || null,
            rawStatus: req.status?.toLowerCase() || "pending", // Store raw status for filtering
            endDateTime: endDateTime, // Store end date/time for expiration checking
          };
        });
        setAccessData(formattedData);
      }
      return res;
    },
  });

  useEffect(() => {
    getRequestsMutation.mutate({ page, limit, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  // Helper function to check if a guest access has expired
  const isExpired = (item: any): boolean => {
    if (!item.endDateTime) return false;
    const now = new Date();
    return item.endDateTime < now;
  };

  // Filter data based on active tab
  const getFilteredData = () => {
    if (!accessData || accessData.length === 0) return [];

    switch (activeTab) {
      case "accepted":
        // Show active/approved guest passes that are NOT expired
        return accessData.filter(
          (item: any) => {
            const isActive =
              item.rawStatus === "active" ||
              item.status?.toLowerCase() === "active" ||
              item.status?.toLowerCase() === "accepted";
            return isActive && !isExpired(item);
          }
        );
      case "pending":
        // Show pending guest passes
        return accessData.filter(
          (item: any) =>
            item.rawStatus === "pending" ||
            item.status?.toLowerCase() === "pending"
        );
      case "rejected":
        // Show rejected/revoked/used guest passes
        return accessData.filter(
          (item: any) =>
            item.rawStatus === "rejected" ||
            item.rawStatus === "revoked" ||
            item.rawStatus === "used" ||
            item.status?.toLowerCase() === "rejected" ||
            item.status?.toLowerCase() === "revoked" ||
            item.status?.toLowerCase() === "used"
        );
      case "expired":
        // Show active guest passes that have expired (end date/time has passed)
        return accessData.filter(
          (item: any) => {
            const isActive =
              item.rawStatus === "active" ||
              item.status?.toLowerCase() === "active" ||
              item.status?.toLowerCase() === "accepted";
            return isActive && isExpired(item);
          }
        );
      default:
        return accessData;
    }
  };

  const filteredAccessData = getFilteredData();

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    // page number clicked
    setPage(pageIndex + 1);

    // ONLY update limit if it actually changed
    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1); // reset page ONLY when page size changes
    }
  };

  const columns: Column<any>[] = [
    { key: "id", title: "ID", width: 120 },
    { key: "guest", title: t("accessRequests.guestName") },
    { key: "start", title: t("accessRequests.startingAt") },
    { key: "end", title: t("accessRequests.endingAt") },
    { key: "otp", title: t("accessRequests.otp") },
    { key: "requestedBy", title: t("accessRequests.requestedBy") },
    { key: "condo", title: t("accessRequests.condoNumber") },
    { key: "req", title: t("accessRequests.requestDateAndTime") },
    {
      key: "status",
      title: t("accessRequests.requestStatus"),
      align: "center",
      render: (value, row) => {
        // Check if the item is expired
        const expired = row.endDateTime && new Date(row.endDateTime) < new Date();
        const displayStatus = expired && (value === "Active" || value === "Accepted") ? "Expired" : value;
        
        const statusLabel = displayStatus === "Active" ? t("common.active") :
                           displayStatus === "Accepted" ? t("accessRequests.accepted") :
                           displayStatus === "Expired" ? t("accessRequests.expired") :
                           displayStatus === "Revoked" ? t("accessRequests.revoked") :
                           displayStatus === "Used" ? t("accessRequests.used") :
                           displayStatus === "Pending" ? t("accessRequests.pending") :
                           displayStatus === "Rejected" ? t("accessRequests.rejected") :
                           displayStatus;
        return (
          <Pills
            label={statusLabel}
            variant={
              displayStatus === "Active" || displayStatus === "Accepted"
                ? "success"
                : displayStatus === "Expired"
                ? "danger"
                : displayStatus === "Revoked" || displayStatus === "Used"
                ? "danger"
                : displayStatus === "Pending"
                ? "warning"
                : "ghost"
            }
            size="sm"
          />
        );
      },
    },
    {
      key: "approveReject",
      title: t("common.actions"),
      align: "center",
      width: 180,
      render: (_value: any, row: any) => {
        const isPending = row.rawStatus === "pending" || row.status?.toLowerCase() === "pending";
        const isApproving = loadingActions[row.id] === "approve";
        const isRejecting = loadingActions[row.id] === "reject";
        
        if (!isPending) {
          return <span style={{ color: "#9CA3AF", fontSize: "14px" }}>-</span>;
        }

        return (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleApproveRequest(row.id);
              }}
              disabled={isApproving || isRejecting}
              style={{
                backgroundColor: isApproving ? "#9CA3AF" : "#10B981",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "6px",
                padding: "6px 16px",
                fontSize: "13px",
                fontWeight: 500,
                cursor: isApproving || isRejecting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                opacity: isApproving || isRejecting ? 0.6 : 1,
              }}
            >
              {isApproving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ width: 14, height: 14, borderWidth: 2 }}
                  />
                  <span>{t("accessRequests.approving")}</span>
                </>
              ) : (
                <span>{t("accessRequests.approveRequest")}</span>
              )}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRejectRequest(row.id);
              }}
              disabled={isApproving || isRejecting}
              style={{
                backgroundColor: isRejecting ? "#9CA3AF" : "#EF4444",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "6px",
                padding: "6px 16px",
                fontSize: "13px",
                fontWeight: 500,
                cursor: isApproving || isRejecting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                opacity: isApproving || isRejecting ? 0.6 : 1,
              }}
            >
              {isRejecting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ width: 14, height: 14, borderWidth: 2 }}
                  />
                  <span>{t("accessRequests.rejecting")}</span>
                </>
              ) : (
                <span>{t("accessRequests.rejectRequest")}</span>
              )}
            </button>
          </div>
        );
      },
    },
    {
      key: "actions",
      title: "",
      align: "left",
      width: 80,
      render: (_value: any, row: any) => (
        <div
          style={{ position: "relative", display: "inline-block", zIndex: 100 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === row.id ? null : row.id);
            }}
            aria-label="actions"
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>
          </button>

          {openMenuId === row.id && (
            <ul
              className="cg-action-dropdown"
              style={{
                position: "absolute",
                right: 0,
                top: "110%",
                zIndex: 1200,
                minWidth: 180,
                background: "#fff",
                boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
                borderRadius: 6,
                padding: 0,
                margin: 0,
                listStyle: "none",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <li
                style={{ padding: "10px 14px", cursor: "pointer" }}
                onClick={() => {
                  setOpenMenuId(null);
                  setSelectedRow(row);
                  setIsViewGuestDetailsModalOpen(true);
                  handleViewDetails(row);
                }}
              >
                {t("accessRequests.viewDetails")}
              </li>
              {row?.status !== "Revoked" && (
                <li
                  style={{
                    padding: "10px 14px",
                    cursor:
                      loadingActions[row.id] === "withdraw"
                        ? "default"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => {
                    if (loadingActions[row.id] === "withdraw") return;
                    setSelectedRow(row);
                    handleWithdrawRequest(row.id);
                  }}
                >
                  {loadingActions[row.id] === "withdraw" && (
                    <span
                      className="spinner-border spinner-border-sm me-2 text-primary"
                      role="status"
                      style={{ width: 16, height: 16 }}
                    />
                  )}
                  {t("accessRequests.withdrawRequest")}
                </li>
              )}
              <li
                style={{
                  padding: "10px 14px",
                  cursor:
                    loadingActions[row.id] === "generateOtp"
                      ? "default"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => {
                  if (loadingActions[row.id] === "generateOtp") return;
                  setSelectedRow(row);
                  handleGenerateOtp(row.id);
                }}
              >
                {loadingActions[row.id] === "generateOtp" && (
                  <span
                    className="spinner-border spinner-border-sm me-2 text-primary"
                    role="status"
                    style={{ width: 16, height: 16 }}
                  />
                )}
                {t("accessRequests.generateNewOTP")}
              </li>
            </ul>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleViewDetails = (row: any) => {
    console.log("View details for", row);
  };

  const handleWithdrawRequest = (guestId: string) => {
    withdrawMutation.mutate(guestId);
  };

  const handleGenerateOtp = (guestId: string) => {
    generateOtpMutation.mutate(guestId);
  };

  const handleApproveRequest = (guestId: string) => {
    approveMutation.mutate(guestId);
  };

  const handleRejectRequest = (guestId: string) => {
    rejectMutation.mutate(guestId);
  };

  const handleSearch = (v: string) => {
    if (v !== lastSearch) {
      setSearch(v);
      setLastSearch(v);
      setPage(1); // Only reset when search changes
    }
  };

  const withdrawMutation = useMutation({
    mutationKey: ["withdraw-guest-access"],
    mutationFn: async (guestId: string) => {
      const response = await WithdrawGuestAccess(guestId);
      if (response && response?.statusCode === 200) {
        getRequestsMutation.mutate({ page, limit, search });
        toast.success(t("accessRequests.requestWithdrawn"));
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            t("accessRequests.failedToWithdraw")
        );
      }
      return response;
    },
    onMutate: (guestId: string) => {
      setLoadingActions((prev) => ({ ...prev, [guestId]: "withdraw" }));
    },
    onSettled: (_data: any, _error: any, guestId: string) => {
      setLoadingActions((prev) => {
        const copy = { ...prev };
        delete copy[guestId];
        return copy;
      });
      setOpenMenuId(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("accessRequests.errorWithdrawing")
      );
    },
  });

  const generateOtpMutation = useMutation({
    mutationKey: ["generate-otp"],
    mutationFn: async (guestId: string) => {
      const response = await GenerateNewOtp(guestId);
      if (response && response?.statusCode === 200) {
        getRequestsMutation.mutate({ page, limit, search });
        toast.success(t("accessRequests.otpGenerated"));
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            t("accessRequests.failedToGenerateOtp")
        );
      }
      return response;
    },
    onMutate: (guestId: string) => {
      setLoadingActions((prev) => ({ ...prev, [guestId]: "generateOtp" }));
    },
    onSettled: (_data: any, _error: any, guestId: string) => {
      setLoadingActions((prev) => {
        const copy = { ...prev };
        delete copy[guestId];
        return copy;
      });
      setOpenMenuId(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("accessRequests.failedToGenerateOtp")
      );
    },
  });

  const approveMutation = useMutation({
    mutationKey: ["approve-guest-pass"],
    mutationFn: async (guestId: string) => {
      const response = await ApproveGuestPass(guestId);
      if (response && response?.statusCode === 200) {
        getRequestsMutation.mutate({ page, limit, search });
        toast.success(t("accessRequests.requestApproved"));
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            t("accessRequests.failedToApprove")
        );
      }
      return response;
    },
    onMutate: (guestId: string) => {
      setLoadingActions((prev) => ({ ...prev, [guestId]: "approve" }));
    },
    onSettled: (_data: any, _error: any, guestId: string) => {
      setLoadingActions((prev) => {
        const copy = { ...prev };
        delete copy[guestId];
        return copy;
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("accessRequests.errorApproving")
      );
    },
  });

  const rejectMutation = useMutation({
    mutationKey: ["reject-guest-pass"],
    mutationFn: async (guestId: string) => {
      const response = await RejectGuestPass(guestId);
      if (response && response?.statusCode === 200) {
        getRequestsMutation.mutate({ page, limit, search });
        toast.success(t("accessRequests.requestRejected"));
      } else {
        throw new Error(
          response?.message ||
            response?.response ||
            t("accessRequests.failedToReject")
        );
      }
      return response;
    },
    onMutate: (guestId: string) => {
      setLoadingActions((prev) => ({ ...prev, [guestId]: "reject" }));
    },
    onSettled: (_data: any, _error: any, guestId: string) => {
      setLoadingActions((prev) => {
        const copy = { ...prev };
        delete copy[guestId];
        return copy;
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("accessRequests.errorRejecting")
      );
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("accessRequests.title")}
            pageTitles={[
              { label: t("accessRequests.dashboard"), path: "/" },
              { label: t("accessRequests.title"), path: "/access-requests" },
            ]}
          />
          <Row>{isFetching ? <Spinner /> : <Widgets data={statsData} />}</Row>
          <Row className="align-items-center justify-content-between">
            <Col className="p-0">
              <Heading
                title={t("accessRequests.manageGuestAccess")}
                description={t("accessRequests.viewAndManage")}
                button={false}
              />
            </Col>
            <Col xs="auto" className="d-flex justify-content-end mb-2">
              <Filter
                value={search}
                onSearch={handleSearch}
                onClear={() => {
                  setSearch("");
                  setLastSearch("");
                  setPage(1);
                }}
              />
            </Col>
            <Col xs="auto" className="d-flex justify-content-end mb-2">
              <Button
                className="btn btn-primary"
                bgColor="#3F51B5"
                onClick={() => setIsOpen(true)}
                icon={
                  <img
                    src="/assets/icons/Add.png"
                    alt="Add Icon"
                    className="text-grey-500"
                  />
                }
                iconPosition="right"
              >
                {t("accessRequests.addGuestAccess")}
              </Button>
            </Col>
          </Row>

          <Nav
            className="bg-gray-600"
            style={{
              borderRadius: "8px",
              display: "inline-flex",
              padding: "3px",
              alignItems: "center",
              backgroundColor: "#F3F4F6",
            }}
          >
            {[
              { key: "accepted", label: t("accessRequests.accepted") },
              { key: "pending", label: t("accessRequests.pending") },
              { key: "rejected", label: t("accessRequests.rejected") },
              { key: "expired", label: t("accessRequests.expired") },
            ].map((tab, idx) => {
              const isActive = activeTab === tab.key;
              return (
                <NavItem key={tab.key}>
                  <NavLink
                    className={classnames({ active: isActive })}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setPage(1); // Reset to first page when tab changes
                    }}
                    style={{
                      cursor: "pointer",
                      padding: "8px 18px",
                      borderRadius: 8,
                      marginRight: idx < 3 ? 8 : 0,
                      transition: "all 120ms ease",
                      fontWeight: 500,
                      fontSize: 14,
                      background: isActive ? "#ffffff" : "transparent",
                      color: isActive ? "#000" : "#6b7280",
                      boxShadow: isActive
                        ? "0 1px 2px rgba(16,24,40,0.06)"
                        : "none",
                      border: isActive
                        ? "1px solid rgba(16,24,40,0.06)"
                        : "none",
                      borderLeft:
                        idx > 0 ? "1px solid rgba(0,0,0,0.06)" : "none",
                      marginLeft: idx > 0 ? 6 : 0,
                      paddingLeft: idx > 0 ? 14 : 18,
                    }}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>

          {getRequestsMutation.isError ? (
            <div className="text-center my-5">
              <div className="text-danger mb-3">
                <i
                  className="ri-error-warning-line"
                  style={{ fontSize: "3rem" }}
                />
              </div>
              <h5 className="text-danger">{t("accessRequests.failedToLoad")}</h5>
              <p className="text-muted">
                {(getRequestsMutation.error as any)?.message ||
                  t("accessRequests.pleaseTryAgain")}
              </p>
              <Button
                className="btn btn-secondary"
                onClick={() =>
                  getRequestsMutation.mutate({ page, limit, search })
                }
              >
                {t("accessRequests.retry")}
              </Button>
            </div>
          ) : (
            <Table
              columns={columns}
              data={filteredAccessData}
              pagination={{
                pageIndex: page - 1,
                pageSize: limit,
                total: filteredAccessData.length,
              }}
              onPageChange={handlePageChange}
              loading={getRequestsMutation.isPending}
              selectable
              onSelectionChange={(sel) => console.log("selected", sel)}
            />
          )}
        </Container>
      </div>
      <AddGuestReqModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        refetchRequests={() =>
          getRequestsMutation.mutate({ page, limit, search })
        }
      />
      <ViewGuestDetailsModal
        isOpen={isViewGuestDetailsModalOpen}
        onClose={() => setIsViewGuestDetailsModalOpen(false)}
        guest={selectedRow}
        refetchRequests={() =>
          getRequestsMutation.mutate({ page, limit, search })
        }
        setAccessData={setAccessData}
      />
    </React.Fragment>
  );
};

export default AccessRequests;
